/**
 * probeVoices — how the Probe page names the examiner's voices.
 *
 * The voice set itself is the contract (`ProbeVoice`, English GPT-Live
 * voices only); this is the label each one wears in the picker. Typed as a
 * record over the contract, so a voice added there cannot ship unlabelled.
 *
 * The GPT-Live voices carry a regional accent of their own (OpenAI's voice
 * table); the classic Realtime voices speak General American.
 *
 * @module components/regulation/probe/probeVoices
 */
import { PROBE_VOICES, type ProbeVoice } from "@amoreg/contracts";

export interface ProbeVoiceLabel {
  readonly name: string;
  /** Accent and presentation, as the picker's second line. */
  readonly description: string;
}

export const PROBE_VOICE_LABELS: Record<ProbeVoice, ProbeVoiceLabel> = {
  vesper: { name: "Vesper", description: "British · masculine" },
  willow: { name: "Willow", description: "Irish · feminine" },
  stone: { name: "Stone", description: "Irish · masculine" },
  quartz: { name: "Quartz", description: "Australian · feminine" },
  ripple: { name: "Ripple", description: "Australian · masculine" },
  gleam: { name: "Gleam", description: "North American · feminine" },
  meridian: { name: "Meridian", description: "North American · masculine" },
  delta: { name: "Delta", description: "Southern US · feminine" },
  cinder: { name: "Cinder", description: "Southern US · masculine" },
  beacon: { name: "Beacon", description: "Filipino English · masculine" },
  marin: { name: "Marin", description: "General American · feminine" },
  cedar: { name: "Cedar", description: "General American · masculine" },
  alloy: { name: "Alloy", description: "General American · neutral" },
  ash: { name: "Ash", description: "General American · masculine" },
  ballad: { name: "Ballad", description: "General American · masculine" },
  coral: { name: "Coral", description: "General American · feminine" },
  echo: { name: "Echo", description: "General American · masculine" },
  sage: { name: "Sage", description: "General American · feminine" },
  shimmer: { name: "Shimmer", description: "General American · feminine" },
  verse: { name: "Verse", description: "General American · masculine" },
};

/** The picker's order: the contract's, which leads with the natural regional voices. */
export const probeVoiceOptions = PROBE_VOICES.map((voice) => ({
  value: voice,
  ...PROBE_VOICE_LABELS[voice],
}));
